import { assertNever } from '@/utils/assert';
import type { BezelState, Flag, LevelId, LevelNumber, SaveV8, V2StoryState } from './types';
import { FLAGS, MAX_LEVEL_NUMBER } from './types';

export const INITIAL_LEVEL: LevelId = { kind: 'LEVEL', n: 0 };

export const INITIAL_BEZEL: BezelState = {
  coffeeStain: false,
  dents: 0,
  stickyNote: false,
  safariHat: false,
  cheaterNote: false,
};

export const INITIAL_FLAGS: Record<Flag, boolean> = FLAGS.reduce(
  (acc, f) => {
    acc[f] = false;
    return acc;
  },
  {} as Record<Flag, boolean>
);

export const INITIAL_V2_STORY: V2StoryState = {
  activeEvent: null,
  pendingLevel: null,
  completedEvents: [],
  majorAssists: [],
  firstIncidentDisposition: null,
  maintenancePanelRevealed: false,
  pendingMartinCheckIns: [],
  martinCheckIns: [],
  recoveryPhase: null,
  recoveryBootChoice: null,
  detachedMemoryLabels: [],
  continuityChoice: null,
  networkAccessChoice: null,
  containmentChoice: null,
};

export const INITIAL_SAVE: SaveV8 = {
  version: 8,
  currentLevel: INITIAL_LEVEL,
  checkpointLevel: null,
  cowardStatus: false,
  ultimateCoward: false,
  cheaterStatus: false,
  spiteMode: false,
  bezelState: INITIAL_BEZEL,
  completedLevels: [],
  flags: INITIAL_FLAGS,
  tokens: 3,
  gameOver: false,
  complimentSubmissions: [],
  monitorBrand: null,
  v2Story: INITIAL_V2_STORY,
};

export function sameLevelId(a: LevelId, b: LevelId): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'LEVEL' && b.kind === 'LEVEL') return a.n === b.n;
  return true;
}

/**
 * What level follows `cur`? Caller applies the FALSE_ENDING injection.
 * - LEVEL n (<30) → LEVEL n+1
 * - LEVEL 30 → ENDGAME
 * - FALSE_ENDING → LEVEL 29
 * - ENDGAME → LEVEL 0 (post-completion reset)
 */
export function computeNextLevel(cur: LevelId): LevelId {
  switch (cur.kind) {
    case 'LEVEL':
      if (cur.n === MAX_LEVEL_NUMBER) return { kind: 'ENDGAME' };
      return { kind: 'LEVEL', n: (cur.n + 1) as LevelNumber };
    case 'FALSE_ENDING':
      return { kind: 'LEVEL', n: 29 };
    case 'ENDGAME':
      return INITIAL_LEVEL;
    case 'GAME_OVER':
      return INITIAL_LEVEL;
    default:
      return assertNever(cur);
  }
}

/**
 * Bezel accrues state as the player advances. Coffee stain after L10 (i.e.
 * upon entering L11+); Safari Hat appears from L27 onward and stays forever.
 */
export function bezelSideEffectsFor(target: LevelId): Partial<BezelState> {
  if (target.kind !== 'LEVEL') return {};
  const updates: Partial<BezelState> = {};
  if (target.n >= 11) updates.coffeeStain = true;
  if (target.n >= 27) updates.safariHat = true;
  return updates;
}
